import { useState, useContext, useEffect, useCallback } from 'react'
import styles from './ClientsTable.module.css'
import { SplitViewContext } from './ManageSection'
import { ClientModal, EditClientModal } from './modals'
import apiClient from '../utils/api'
import { formatDisplayDate } from '../utils/dateUtils'

export default function ClientsTable({ apiToken, user }) {
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('active')
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)
    const [selectedClient, setSelectedClient] = useState(null)
    const [clients, setClients] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [searchTimeout, setSearchTimeout] = useState(null)
    const [isAdmin, setIsAdmin] = useState(false)
    const [checkingAdmin, setCheckingAdmin] = useState(true)
    
    // Get split view context
    const splitViewContext = useContext(SplitViewContext)
    const { setIsInSplitView } = splitViewContext || {}
    
    // Update split view state when client selection changes
    useEffect(() => {
        if (setIsInSplitView) {
            setIsInSplitView(!!selectedClient)
        }
    }, [selectedClient, setIsInSplitView])

    // Check if user is admin by trying to fetch teammates
    useEffect(() => {
        const checkAdminStatus = async () => {
            if (!apiToken) return
            
            try {
                setCheckingAdmin(true)
                apiClient.setAuthToken(apiToken)
                await apiClient.getTeammates()
                setIsAdmin(true) // If request succeeds, user is admin
            } catch (error) {
                setIsAdmin(false) // If request fails, user is not admin
            } finally {
                setCheckingAdmin(false)
            }
        }
        
        checkAdminStatus()
    }, [apiToken])

    // Load clients from API
    const loadClients = useCallback(async (searchQuery = '') => {
        if (!apiToken) return
        
        try {
            setLoading(true)
            setError(null)
            apiClient.setAuthToken(apiToken)
            
            const params = {}
            if (searchQuery.trim()) {
                params.search = searchQuery.trim()
            }
            if (statusFilter && statusFilter !== 'all') {
                params.status = statusFilter
            }
            
            const response = await apiClient.getClients(params)
            setClients(response.clients || [])
        } catch (err) {
            setError(err.message || 'Failed to load clients')
            console.error('Failed to load clients:', err)
        } finally {
            setLoading(false)
        }
    }, [apiToken, statusFilter])

    // Load clients on mount and when filters change
    useEffect(() => {
        loadClients()
    }, [loadClients])

    // Handle search with debouncing
    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm)
        
        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout)
        }
        
        // Set new timeout for API call
        const timeout = setTimeout(() => {
            loadClients(newSearchTerm)
        }, 500) // 500ms delay
        
        setSearchTimeout(timeout)
    }

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout)
            }
        }
    }, [searchTimeout])

    // Filter is now done on backend, so just use the clients array directly
    const filteredClients = clients

    // Handle client save (create or update)
    const handleClientSave = (savedClient) => {
        // Find if this client already exists (for updates)
        const existingIndex = clients.findIndex(c => c.id === savedClient.id)
        
        if (existingIndex >= 0) {
            // Update existing client
            const updatedClients = [...clients]
            updatedClients[existingIndex] = savedClient
            setClients(updatedClients)
        } else {
            // Add new client
            setClients([savedClient, ...clients])
        }
        
        // If this was the selected client, update the selection
        if (selectedClient && selectedClient.id === savedClient.id) {
            setSelectedClient(savedClient)
        }
    }

    const getStatusBadge = (status) => {
        const statusClass = {
            'active': styles.statusActive,
            'inactive': styles.statusInactive,
            'archived': styles.statusArchived,
            'on-hold': styles.statusOnHold
        }[status] || styles.statusActive

        const statusText = {
            'active': 'Active',
            'inactive': 'Inactive',
            'archived': 'Archived',
            'on-hold': 'On Hold'
        }[status] || status

        return (
            <span className={`${styles.statusBadge} ${statusClass}`}>
                {statusText}
            </span>
        )
    }

    // Use centralized date formatting
    const formatDate = (dateString) => {
        return formatDisplayDate(dateString)
    }

    const handleClientSelect = (client) => {
        setSelectedClient(client)
    }

    const handleCloseDetails = () => {
        setSelectedClient(null)
    }

    const handleEditClient = () => {
        setShowEditModal(true)
    }

    const handleArchiveClient = async () => {
        if (!selectedClient) return

        const confirmMessage = selectedClient.status === 'archived'
            ? `Are you sure you want to unarchive "${selectedClient.name}"?`
            : `Are you sure you want to archive "${selectedClient.name}"?`

        if (!confirm(confirmMessage)) return

        try {
            apiClient.setAuthToken(apiToken)
            const newStatus = selectedClient.status === 'archived' ? 'active' : 'archived'

            const response = await apiClient.updateClient(selectedClient.id, {
                status: newStatus
            })

            const updatedClient = response.client

            // Refetch clients to ensure the list stays accurate with current filter
            await loadClients(searchTerm)

            // Update selected client or close details if it no longer matches filter
            if (statusFilter === 'all' || updatedClient.status === statusFilter) {
                setSelectedClient(updatedClient)
            } else {
                setSelectedClient(null)
            }

            // Show success message
            alert(newStatus === 'archived'
                ? 'Client archived successfully'
                : 'Client unarchived successfully')
        } catch (err) {
            console.error('Failed to update client status:', err)
            alert('Failed to update client status. Please try again.')
        }
    }

    const renderClientDetails = () => {
        if (!selectedClient) return null

        return (
            <div className={styles.clientDetails}>
                <div className={styles.detailsHeader}>
                    <h2>{selectedClient.name}</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCloseDetails}
                        aria-label="Close client details"
                    >
                        ×
                    </button>
                </div>
                
                <div className={styles.detailsContent}>
                    <div className={styles.detailSection}>
                        <h3>Client Information</h3>
                        <div className={styles.detailGrid}>
                            <div className={styles.detailItem}>
                                <label>Status</label>
                                <span>{getStatusBadge(selectedClient.status)}</span>
                            </div>
                            <div className={styles.detailItem}>
                                <label>Currency</label>
                                <span>{selectedClient.currency}</span>
                            </div>
                            <div className={styles.detailItem}>
                                <label>Date Format</label>
                                <span>{selectedClient.date_format || 'MM/DD/YYYY'}</span>
                            </div>
                            <div className={styles.detailItem}>
                                <label>Total Projects</label>
                                <span>{selectedClient.projects_count}</span>
                            </div>
                            <div className={styles.detailItem}>
                                <label>Created</label>
                                <span>{formatDate(selectedClient.created_at)}</span>
                            </div>
                        </div>
                    </div>
                    
                    {/*<div className={styles.detailSection}>*/}
                    {/*    <h3>Contact Information</h3>*/}
                    {/*    <div className={styles.detailGrid}>*/}
                    {/*        <div className={styles.detailItem}>*/}
                    {/*            <label>Email</label>*/}
                    {/*            <span>{selectedClient.email}</span>*/}
                    {/*        </div>*/}
                    {/*        <div className={styles.detailItem}>*/}
                    {/*            <label>Phone</label>*/}
                    {/*            <span>{selectedClient.phone}</span>*/}
                    {/*        </div>*/}
                    {/*        <div className={styles.detailItem}>*/}
                    {/*            <label>Address</label>*/}
                    {/*            <span>{selectedClient.address}</span>*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*</div>*/}
                    
                    {/*<div className={styles.detailSection}>*/}
                    {/*    <h3>Description</h3>*/}
                    {/*    <p>{selectedClient.description}</p>*/}
                    {/*</div>*/}
                    
                    <div className={styles.detailSection}>
                        <h3>Actions</h3>
                        <div className={styles.detailActions}>
                            {isAdmin && (
                                <button
                                    className={styles.primaryButton}
                                    onClick={handleEditClient}
                                >
                                    Edit Client
                                </button>
                            )}
                            <button className={styles.secondaryButton}>
                                View Projects
                            </button>
                            <button className={styles.secondaryButton}>
                                Add New Project
                            </button>
                            {isAdmin && (
                                <button
                                    className={selectedClient.status === 'archived' ? styles.warningButton : styles.dangerButton}
                                    onClick={handleArchiveClient}
                                >
                                    {selectedClient.status === 'archived' ? 'Unarchive Client' : 'Archive Client'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={`${styles.clientsContainer} ${selectedClient ? styles.split : ''}`}>
            <div className={`${styles.splitView} ${selectedClient ? styles.split : ''}`}>
                {/* Main Clients Content */}
                <div className={selectedClient ? styles.clientsSidebar : styles.clientsMain}>
                    {!selectedClient && (
                        <div className={styles.header}>
                            <div className={styles.headerLeft}>
                                <h2>Clients</h2>
                                <p>Manage and track your clients</p>
                            </div>
                            {isAdmin && (
                                <button 
                                    className={styles.createButton}
                                    onClick={() => setShowCreateModal(true)}
                                    disabled={checkingAdmin}
                                >
                                    + Create Client
                                </button>
                            )}
                        </div>
                    )}

                    {/* Conditional Content: Show full table or simple list based on selection */}
                    {!selectedClient ? (
                        <>
                            {/* Search and Filters */}
                            <div className={styles.controls}>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className={styles.filterSelect}
                                    disabled={loading}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="archived">Archived</option>
                                    <option value="all">All Status</option>
                                </select>
                                <div className={styles.searchBox}>
                                    <input
                                        type="text"
                                        placeholder="Search clients..."
                                        value={searchTerm}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        className={styles.searchInput}
                                        disabled={loading}
                                    />
                                    <span className={styles.searchIcon}>
                                        {loading ? '⏳' : '🔍'}
                                    </span>
                                </div>

                                {error && (
                                    <div className={styles.errorMessage}>
                                        {error}
                                    </div>
                                )}
                            </div>

                            {/* Clients Table */}
                            <div className={styles.tableContainer}>
                                <table className={styles.clientsTable}>
                                    <thead>
                                        <tr>
                                            <th>Client Name</th>
                                            <th>Currency</th>
                                            <th>Date Format</th>
                                            <th>Projects</th>
                                            <th>Status</th>
                                            <th>Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="7" className={styles.loading}>
                                                    Loading clients...
                                                </td>
                                            </tr>
                                        ) : filteredClients.length > 0 ? (
                                            filteredClients.map(client => (
                                                <tr
                                                    key={client.id}
                                                    onClick={() => handleClientSelect(client)}
                                                    className={styles.tableRow}
                                                >
                                                    <td>
                                                        <div className={styles.clientName}>
                                                            <span className={styles.name}>{client.name}</span>
                                                        </div>
                                                    </td>
                                                    <td>{client.currency}</td>
                                                    <td>{client.date_format || 'MM/DD/YYYY'}</td>
                                                    <td>{client.projects_count}</td>
                                                    <td>{getStatusBadge(client.status)}</td>
                                                    <td>{formatDate(client.created_at)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="7" className={styles.noResults}>
                                                    {searchTerm ? `No clients found matching "${searchTerm}"` : 'No clients found'}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        /* Simple Clients List for Split View */
                        <div className={styles.clientsListContainer}>
                            <div className={styles.listHeader}>
                                <h3>Clients</h3>
                                <div className={styles.listHeaderRight}>
                                    <span className={styles.clientCount}>{filteredClients.length} clients</span>
                                    {isAdmin && (
                                        <button 
                                            className={styles.createButtonCompact}
                                            onClick={() => setShowCreateModal(true)}
                                            aria-label="Create Client"
                                            title="Create Client"
                                            disabled={checkingAdmin}
                                        >
                                            +
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className={styles.clientsList}>
                                {filteredClients.map(client => (
                                    <div 
                                        key={client.id}
                                        className={`${styles.clientListItem} ${client.id === selectedClient.id ? styles.active : ''}`}
                                        onClick={() => handleClientSelect(client)}
                                    >
                                        <div className={styles.clientListName}>{client.name}</div>
                                        <div className={styles.clientListIndustry}>{client.currency} • {client.projects_count} projects</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Client Details Panel */}
                {selectedClient && renderClientDetails()}
            </div>

            {/* Create Client Modal */}
            <ClientModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSave={handleClientSave}
                apiToken={apiToken}
            />

            {/* Edit Client Modal */}
            <EditClientModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                onSave={handleClientSave}
                client={selectedClient}
                apiToken={apiToken}
            />
        </div>
    )
}